import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/magazine/Breadcrumbs';
import AuthorBlock from '../components/magazine/AuthorBlock';
import ArticleCard from '../components/magazine/ArticleCard';
import { listArticles, getAuthor } from '../lib/magazineApi';

export default function MagazineAuthorPage() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([getAuthor(slug), listArticles({ author: slug, page_size: 24 })])
      .then(([a, list]) => {
        setAuthor(a);
        setArticles(list.items || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <p className="text-center text-neutral-500">Loading…</p>
      </main>
    );
  }
  if (error || !author) {
    return (
      <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24">
        <div className="text-center max-w-md mx-auto px-6">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">404</p>
          <h1 className="mt-3 text-3xl font-bold text-neutral-900">Author not found</h1>
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1 mt-6 text-[#F77F11] hover:text-[#d8651a] font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Magazine
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24" data-testid="magazine-author-page">
      <SEO
        path={`/magazine/author/${author.slug}`}
        title={`${author.name} | Kitxens Magazine`}
        description={author.bio || `${author.name} writes for Kitxens Magazine.`}
      />

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="pt-2 pb-6">
          <Breadcrumbs
            items={[
              { label: 'Magazine', href: '/magazine' },
              { label: author.name },
            ]}
          />
        </div>

        <div className="max-w-3xl mb-12">
          <AuthorBlock author={author} />
        </div>

        <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-6">
          Articles by {author.name} ({articles.length})
        </h2>
        {articles.length === 0 ? (
          <p className="text-center text-neutral-500 py-16">No articles yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
